import {
  observable,
  computed,
  action,
  runInAction,
  autorun,
  comparer,
  makeObservable,
} from 'mobx'
import uniqBy from 'lodash/uniqBy'
import {
  isCenterpiece,
  type MATERIAL_TYPES,
  type TILE_SIZES,
  type TILE_CONTENT,
} from '@mixtiles/web-backend-shared'
import { isClient } from 'utils/runtimeUtils'
import { calculateAllPossibleRatios } from 'utils/cropRatioUtils'
import { type CropAlignment } from 'utils/cropAlignment.consts'
import { isFramedMaterial } from 'utils/materialTypeUtils'
import StorageManager from 'services/StorageManager'
import { logger } from 'services/logger'
import { callApi } from 'api/apiProvider'
import { saveOrderDraftApi } from 'api/orderDraft.api'
import { filterTileParams } from 'components/PhotoBank/tileUtils'
import {
  TILES_STORAGE_KEY,
  PENDING_TILES_STORAGE_KEY,
} from 'components/TilesProvider/TilesProvider.consts'
import type { PhotoBankStore } from '../photoBankStore/PhotoBankStore'
import { type BaseTileStore, type TilePhotoUrl } from './tiles/BaseTileStore'
import {
  parseTileStoreFromTileData,
  parseTileStoresFromRawTiles,
  instantiateTile,
} from './tiles/tileFactory'
import { type TileCrop, type TileData } from './tiles/tileSchemas'
import { readRawTilesFromStorage } from './tiles/tileStorage'
import { BulkCustomizationStore } from './BulkCustomizationStore'
import { type Tile } from 'components/TilesProvider/TilesProvider.types'
import { type TilePatch, type TilePatchMode } from './TilePatch'
import { type StylingFields } from './TilesStore.types'
import {
  FALLBACK_NOT_CLASSIC_MATERIAL_STYLE,
  FALLBACK_STYLE,
} from 'pages/PhotoStyler/TileDesignerConsts'

type AdjustCropInput = {
  uid?: string
  cropRect?: TileCrop
  aspectRatio: number
  originalWidth: number
  originalHeight: number
  cropAlignment?: CropAlignment
}

export type CropAdjuster = (params: AdjustCropInput) => TileCrop

export class TilesStore {
  readonly photoBankStore: PhotoBankStore

  @observable.shallow accessor tilesMap = new Map<string, BaseTileStore>()

  // Gates backend sync: true only after the account's tiles are merged in, so we never push before we pull.
  @observable accessor isAccountSyncReady = false

  @observable.ref accessor cropAdjuster: CropAdjuster | undefined

  @observable accessor hasLoadedLocalTiles = false

  bulkCustomizationStore!: BulkCustomizationStore

  constructor(photoBankStore: PhotoBankStore) {
    this.photoBankStore = photoBankStore

    if (isClient()) {
      this.initializeFromStorage()
      // `bulkCustomizationStore` depends on client-only APIs and is intentionally unavailable during SSR.
      this.bulkCustomizationStore = new BulkCustomizationStore(this)
      this.setupAutorun()
    }
    makeObservable(this)
  }

  @computed
  get nonCenterpieceTiles(): BaseTileStore[] {
    return this.tiles.filter((t) => !isCenterpiece(t))
  }

  @computed
  get tiles(): BaseTileStore[] {
    return Array.from(this.tilesMap.values())
  }

  @computed
  get providerTiles(): Tile[] {
    return this.tiles.map((t) => t.providerSnapshot as Tile)
  }

  get fallbackStyle(): StylingFields {
    if (this.tiles.some((t) => !isFramedMaterial(t.materialType))) {
      return FALLBACK_NOT_CLASSIC_MATERIAL_STYLE as StylingFields
    }
    return FALLBACK_STYLE as StylingFields
  }

  // Restore the account's tiles, then enable sync — so the first backend write reflects the
  // merged set, never an empty not-yet-loaded state. Returns restored tiles for the caller's toast.
  @action
  onAccountSyncReady(rawTiles: Record<string, unknown>[]): BaseTileStore[] {
    const restored = this.restoreRawTiles(rawTiles)
    this.isAccountSyncReady = true
    return restored
  }

  @action
  onSignedOut() {
    this.isAccountSyncReady = false
  }

  @action
  setCropAdjuster(cropAdjuster?: CropAdjuster) {
    this.cropAdjuster = cropAdjuster
  }

  @action
  addTile(params: Record<string, unknown>): BaseTileStore {
    const tile = this.parseTile(params)

    this.tilesMap.set(tile.id, tile)
    return tile
  }

  // Tiles render in Map insertion order, so rebuild the map with the new entry spliced in after
  // the reference tile. Appends if the reference isn't found.
  @action
  addTileAfter(
    referenceTileId: string,
    params: Record<string, unknown>
  ): BaseTileStore {
    const tile = this.parseTile(params)
    const entries = Array.from(this.tilesMap.entries())
    const index = entries.findIndex(([id]) => id === referenceTileId)

    if (index === -1) {
      this.tilesMap.set(tile.id, tile)
      return tile
    }

    // This is not heavy mobx wise because we do it in a single action
    entries.splice(index + 1, 0, [tile.id, tile])
    this.tilesMap.clear()
    for (const [id, tileStore] of entries) {
      this.tilesMap.set(id, tileStore)
    }
    return tile
  }

  @action
  addTiles(
    tilesParams: Array<{ tileContent: TILE_CONTENT } & Record<string, unknown>>
  ): BaseTileStore[] {
    const newTiles = this.parseTiles(tilesParams)
    for (const tile of newTiles) {
      this.tilesMap.set(tile.id, tile)
    }
    return newTiles
  }

  @action
  removeTile(tileId: string) {
    this.tilesMap.delete(tileId)
  }

  @action
  replaceTile(replaceId: string, data: TilePatch) {
    this.applyPatch(replaceId, data, 'replace')
  }

  @action
  updateTiles(tileIds: string[], updateFn: (tile: Tile) => TilePatch) {
    if (tileIds.length === 0) return

    for (const tileId of tileIds) {
      const tile = this.tilesMap.get(tileId)
      if (tile) {
        const patch = updateFn(tile.providerSnapshot as Tile)
        tile.applyPatch(patch, 'update')
      }
    }
  }

  @action
  clearTiles() {
    this.tilesMap.clear()
  }

  // For logout paths that reload immediately: wipes synchronously (can't wait for the
  // debounced persist) and clears storage so tiles don't leak into the next account.
  @action
  clearMemoryAndStorage() {
    this.tilesMap.clear()
    this.isAccountSyncReady = false
    StorageManager.remove(TILES_STORAGE_KEY)
    StorageManager.remove(PENDING_TILES_STORAGE_KEY)
  }

  @action
  resetTiles(tiles: BaseTileStore[]) {
    this.tilesMap.clear()
    for (const tile of tiles) {
      this.tilesMap.set(tile.id, tile)
    }
  }

  @action
  restoreTiles(tiles: TileData[]): BaseTileStore[] {
    const restored = tiles
      .filter((tile) => !this.tilesMap.has(tile.id))
      .map((tile) => instantiateTile(tile, this.photoBankStore))
      .filter((tile) => tile.hasValidRequiredPhotos())

    for (const tile of restored) {
      this.tilesMap.set(tile.id, tile)
    }

    return restored
  }

  @action
  restoreRawTiles(rawTiles: Record<string, unknown>[]): BaseTileStore[] {
    const nonDuplicateTiles = rawTiles.filter(
      (t) => !this.tilesMap.has(t.id as string)
    )
    const restored = this.parseTiles(nonDuplicateTiles).filter((t) =>
      t.hasValidRequiredPhotos()
    )

    if (restored.length > 0) {
      for (const tile of restored) {
        this.tilesMap.set(tile.id, tile)
      }
    }

    return restored
  }

  @action
  changeTileSize(tileId: string, tileSize: TILE_SIZES) {
    const tile = this.tilesMap.get(tileId)
    if (tile) {
      tile.updateSize(tileSize)
    }
  }

  @action
  changeTileSizeToAll(tileSize: TILE_SIZES) {
    for (const tile of this.tiles) {
      tile.updateSize(tileSize)
    }
  }

  @action
  applyTileMaterial(tileId: string, materialType: MATERIAL_TYPES) {
    const tile = this.tilesMap.get(tileId)
    if (tile) {
      tile.applyMaterial(materialType)
    }
  }

  @action
  applyTilesMaterial(materialType: MATERIAL_TYPES) {
    for (const tile of this.tiles) {
      tile.applyMaterial(materialType)
    }
  }

  @action
  applyTileStyling(tileId: string, style: Partial<StylingFields>) {
    const tile = this.tilesMap.get(tileId)
    if (tile) {
      tile.applyFullStyling(style)
    }
  }

  @action
  applyTilesStyling(style: Partial<StylingFields>) {
    for (const tile of this.tiles) {
      tile.applyFullStyling(style)
    }
  }

  getTile(tileId: string): BaseTileStore {
    const tile = this.tilesMap.get(tileId)
    if (!tile) {
      throw new Error(`Tile ${tileId} is not in the tiles store`)
    }
    return tile
  }

  tryGetTile(tileId: string): BaseTileStore | undefined {
    return this.tilesMap.get(tileId)
  }

  @action
  async getTilePhotosUrls(tiles: BaseTileStore[]): Promise<TilePhotoUrl[]> {
    const photoUrls = await Promise.all(
      tiles.map((tile) => tile.getPhotoUrls())
    )

    return photoUrls.flat()
  }

  getTilePhotosUids(): string[] {
    return this.tiles.flatMap((tile) => tile.getPhotoUids())
  }

  @action
  private applyPatch(tileId: string, patch: TilePatch, mode: TilePatchMode) {
    this.tilesMap.get(tileId)?.applyPatch(patch, mode)
  }

  // Parses raw tile data into a list of BaseTileStore instances
  // Does not add the tiles to the tilesMap
  parseTiles(rawTiles: Record<string, unknown>[]): BaseTileStore[] {
    const parsedTiles: BaseTileStore[] = []

    for (const raw of rawTiles) {
      try {
        parsedTiles.push(this.parseTile(raw))
      } catch (error) {
        logger.error(
          'Failed to parse tile from storage, skipping tile',
          error,
          {
            rawTile: raw,
          }
        )
      }
    }

    return parsedTiles
  }

  parseTile(raw: Record<string, unknown>): BaseTileStore {
    return parseTileStoreFromTileData(raw, this.photoBankStore)
  }

  private areCropParamsEqual(
    cropParams: Record<number, TileCrop>,
    adjustedCropParams: Record<number, TileCrop>
  ): boolean {
    return Object.entries(adjustedCropParams).every(([aspectRatio, crop]) =>
      comparer.structural(cropParams[Number(aspectRatio)], crop)
    )
  }

  private initializeFromStorage() {
    runInAction(() => {
      const tileStores = parseTileStoresFromRawTiles(
        readRawTilesFromStorage(),
        this.photoBankStore
      )

      for (const tileStore of tileStores) {
        this.tilesMap.set(tileStore.id, tileStore)
      }

      this.hasLoadedLocalTiles = true
    })
  }

  private serializeTilesForStorage(): {
    readyTiles: Record<string, unknown>[]
    pendingTiles: Record<string, unknown>[]
  } {
    const readyTiles: Record<string, unknown>[] = []
    const pendingTiles: Record<string, unknown>[] = []

    for (const tile of this.tiles) {
      const serialized = tile.toJSON()
      switch (tile.getPersistenceState()) {
        case 'ready':
          readyTiles.push(serialized)
          break
        case 'pending':
          pendingTiles.push(serialized)
          break
        case 'skip':
          break
      }
    }

    return {
      readyTiles: uniqBy(readyTiles, 'id'),
      pendingTiles: uniqBy(pendingTiles, 'id'),
    }
  }

  private setupAutorun() {
    this.setupCropAdjustmentAutorun()
    this.setupPersistToLocalStorageAutorun()
    this.setupSyncToBackendAutorun()
  }

  private setupPersistToLocalStorageAutorun() {
    autorun(
      () => {
        const { readyTiles, pendingTiles } = this.serializeTilesForStorage()

        try {
          if (readyTiles.length === 0) {
            StorageManager.remove(TILES_STORAGE_KEY)
          } else {
            StorageManager.set(TILES_STORAGE_KEY, readyTiles)
          }

          if (pendingTiles.length > 0) {
            StorageManager.set(PENDING_TILES_STORAGE_KEY, pendingTiles)
          } else {
            StorageManager.remove(PENDING_TILES_STORAGE_KEY)
          }
        } catch (err) {
          logger.error('Error persisting tiles to storage', err)
        }
      },
      {
        name: 'TilesStore:persistToLocalStorage',
        delay: 150,
      }
    )
  }

  private setupSyncToBackendAutorun() {
    autorun(
      () => {
        if (!this.isAccountSyncReady) return

        const { readyTiles } = this.serializeTilesForStorage()
        callApi(
          saveOrderDraftApi({
            tiles: readyTiles.map(filterTileParams) as unknown as Tile[],
          })
        ).catch((err) => {
          logger.error('Failed to sync tiles to backend', err, {
            'Tiles Count': readyTiles.length,
          })
        })
      },
      {
        name: 'TilesStore:syncToBackend',
        delay: 1000,
      }
    )
  }

  private setupCropAdjustmentAutorun() {
    autorun(
      () => {
        const adjustCrop = this.cropAdjuster
        if (!adjustCrop) return

        const aspectRatios = calculateAllPossibleRatios()

        for (const tile of this.tiles) {
          const context = tile.getCropAdjustmentContext()
          if (!context) continue

          const { cropParams } = tile
          const adjustedCropParams: Record<number, TileCrop> = {}

          aspectRatios.forEach((aspectRatio) => {
            adjustedCropParams[aspectRatio] = adjustCrop({
              uid: context.uid,
              cropRect: cropParams[aspectRatio],
              aspectRatio,
              originalWidth: context.width,
              originalHeight: context.height,
              cropAlignment: context.initialCropAlignment,
            })
          })

          if (!this.areCropParamsEqual(cropParams, adjustedCropParams)) {
            Object.entries(adjustedCropParams).forEach(
              ([aspectRatio, crop]) => {
                tile.updateCropParams(Number(aspectRatio), crop)
              }
            )
          }
        }
      },
      {
        name: 'TilesStore:adjustTileCrops',
      }
    )
  }
}
